import type { NextPage, GetStaticProps } from 'next'
import { FC, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { TertiaryButtonLink } from 'ui'

import AuthLayout, { AuthBox } from '../components/AuthLayout'
import SignUpForm from '../components/sign-up/form'
import AuthCodeScreen from '../components/AuthCodeScreen'
import { PageProps } from './[slug]'

import getGlobalFlags from '../services/flags/getGlobalFlags'
import useAuth from '../hooks/useAuth'
import { USER_TYPE } from '../services/customer/loadAttributes'
import { useTracking } from '../services/context/TrackingContext'

import { usePreferredHomesRoute } from '../hooks/use-preferred-homes-route'


const SignUpStep: FC<{ className?: string }> = ({ className, children }) => {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {children}
    </motion.div>
  )
}

const SignUp: NextPage<PageProps> = () => {
  const { t } = useTranslation('auth')
  const router = useRouter()
  const { returnUrl } = router.query

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const { track, identify } = useTracking()

  const [trackedComplete, setTrackedComplete] = useState(false)

  const homesRoute = usePreferredHomesRoute()

  const { 
    user,
    isAuthenticated,
    isLoading,
    unverifiedAccount,
    authRedirect 
  } = useAuth()

  useEffect(() => {
    if (user && !trackedComplete) {
      identify({
        traits: {
          account_creation_method: user.identityProvider ?? 'Manual',
          sms_consent_bit: user.allowSms ? 1 : 0,
          where_are_you_planning_to_buy: user.marketPreference,
          first_name: user.firstName,
          last_name: user.lastName,
          name: `${user.firstName} ${user.lastName}`,
          email: user.email.toLowerCase(),
          phone: user.phoneNumber,
          real_state_agent_bit: user.userType === USER_TYPE.AGENT ? 1 : 0,
          /** 
           * @TODO this will overwrite if signed in user comes back to sign up. Rare, but should save account 
           * create time on cognito user to prevent
           */
          account_created_at: new Date().getTime(),
          ...(!user.identityProvider ? { email_verification_bit: 1 } : {})
        },
      })
      track({
        event: 'Sign Up Flow Completed',
        properties: {
          form_name: 'Account Creation',
          form_step_count: 2,
          form_step_id: 2,
          form_step_name: 'Email Verification',
          form_field_count: 1,
          form_fields: [],
        },
      })

      setTrackedComplete(true)
      authRedirect()
    }
  }, [user, track, trackedComplete, identify, authRedirect])

  return (
    <AuthLayout
      mobileHeading={t('sign-up')}
      // @TODO this should be a redirect to where they came from
      close={homesRoute}
      trackClose
      back={() => router.back}
      hasFooter
      className='bg-white'
      loading={isLoading || isAuthenticated}
      data-testid={isAuthenticated ? 'redirecting' : null}
    >
      <AnimatePresence>
        <AuthBox>
          {unverifiedAccount.email === '' ? (
            <SignUpStep>
              <h1 className="mb-20 hidden text-center hd-lg md:block">
                {t('sign-up')}
              </h1>
              <p className="mb-20 text-16 text-center leading-snug tracking-1">
                {t('intro.description')}
              </p>
              <SignUpForm
                isLoading={isLoading || isAuthenticated}
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                back={router.back}
              />
              <div className="mt-16 mx-auto w-full max-w-420 space-y-24 text-center">
                <p className="text-14 text-grey-7">
                  {t('have-an-account')}
                  <Link href={returnUrl ? `/sign-in?returnUrl=${returnUrl}` : '/sign-in'} passHref>
                    <TertiaryButtonLink
                      className="ml-8"
                      onClick={() =>
                        track({
                          event: 'Sign Up Flow Dismissed',
                          properties: {
                            form_name: 'Account Creation',
                            form_step_count: 2,
                            form_step_id: 1,
                            form_step_name: 'Account Creation',
                            reason: 'Navigated to Sign In Form',
                          },
                        })
                      }
                    >
                      {t('sign-in')}
                    </TertiaryButtonLink>
                  </Link>
                </p>
              </div>
            </SignUpStep>
          ) : (
            <SignUpStep>
              <AuthCodeScreen
                email={email || unverifiedAccount.email}
                password={password}
              />
            </SignUpStep>            
          )}
        </AuthBox>
      </AnimatePresence>
    </AuthLayout>
  )
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const getStaticProps: GetStaticProps = async ({ locale = '' }: any) => {
  const globalFlags = await getGlobalFlags()

  return {
    props: {
      ...globalFlags,
      ...(await serverSideTranslations(locale, ['common', 'auth'])),
    },
  }
}

export default SignUp
